import {
	createContext,
	FunctionComponent,
	ReactNode,
	useCallback,
	useContext,
	useLayoutEffect,
	useMemo,
	useState,
} from "react";

/**
 * Lets the app know whether the page on screen renders the help launcher in its own header.
 *
 * Most surfaces have a header bar to hang the question-mark icon on (`PageHeader`, `FeedTopbar`,
 * `ListContextHeader`, the List and Account title bars), but a handful — the dashboard, the tracker
 * pages, the onboarding flows — have none. Those get an identical icon pinned to the same top-right
 * spot instead, and this registry is what keeps the two from ever showing at once.
 */
interface HelpLauncherRegistry {
	/** Called by a header-mounted launcher; returns the unregister for its cleanup. */
	registerHeaderLauncher: () => () => void;
	/** True while at least one header on screen renders its own launcher. */
	hasHeaderLauncher: boolean;
}

const HelpLauncherRegistryContext = createContext<HelpLauncherRegistry | null>(null);

export const HelpLauncherRegistryProvider: FunctionComponent<{ children: ReactNode }> = ({
	children,
}) => {
	const [headerLauncherCount, setHeaderLauncherCount] = useState(0);

	const registerHeaderLauncher = useCallback(() => {
		setHeaderLauncherCount((count) => count + 1);

		return () => setHeaderLauncherCount((count) => count - 1);
	}, []);

	const value = useMemo(
		() => ({ registerHeaderLauncher, hasHeaderLauncher: headerLauncherCount > 0 }),
		[registerHeaderLauncher, headerLauncherCount]
	);

	return (
		<HelpLauncherRegistryContext.Provider value={value}>
			{children}
		</HelpLauncherRegistryContext.Provider>
	);
};

/**
 * Announces a header-mounted launcher for as long as it is on screen.
 *
 * Registration runs in a **layout** effect so it lands before the browser paints — a passive effect
 * would let the pinned fallback flash for a frame on every route change.
 */
export const useRegisterHeaderLauncher = (isRendered: boolean): void => {
	const registry = useContext(HelpLauncherRegistryContext);

	useLayoutEffect(() => {
		if (!registry || !isRendered) {
			return;
		}

		return registry.registerHeaderLauncher();
	}, [registry, isRendered]);
};

/** Whether a header on screen already shows the launcher, so the pinned one should stand down. */
export const useHasHeaderLauncher = (): boolean =>
	useContext(HelpLauncherRegistryContext)?.hasHeaderLauncher ?? false;
